import { Eye } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { formatShortTicket } from '../../../lib/utils'
import {
  ClientItem,
  ClientPurchasesResult,
  clientsService,
} from '../../../services/clients.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { ImagePreviewModal } from '../../ui/ImagePreviewModal'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { GlobalPaginator } from '../../ui/global-paginator'

interface ClientDetailModalProps {
  client: ClientItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const emptyPurchases: ClientPurchasesResult = {
  data: [],
  currentPage: 1,
  lastPage: 1,
  perPage: 10,
  total: 0,
}

const money = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
})

const dateTime = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatDate(value: string | null) {
  return value ? dateTime.format(new Date(value)) : '—'
}

export function ClientDetailModal({
  client,
  open,
  onOpenChange,
}: ClientDetailModalProps) {
  const [profile, setProfile] = useState<ClientItem | null>(client)
  const [purchases, setPurchases] =
    useState<ClientPurchasesResult>(emptyPurchases)
  const [page, setPage] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false)
  const [purchasesError, setPurchasesError] = useState(false)
  const [purchasesAttempt, setPurchasesAttempt] = useState(0)
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  useEffect(() => {
    setPreviewImage(null)
  }, [client?.id, open])

  useEffect(() => {
    if (!open || !client) return
    let active = true
    setProfile(client)
    setPage(1)
    setStartDate('')
    setEndDate('')
    setIsLoadingProfile(true)

    clientsService
      .getItem(client.id)
      .then((data) => {
        if (active) setProfile(data)
      })
      .catch(() => toast.error('No se pudo cargar el perfil del cliente'))
      .finally(() => {
        if (active) setIsLoadingProfile(false)
      })

    return () => {
      active = false
    }
  }, [client, open])

  useEffect(() => {
    if (!open || !client) return
    let active = true
    setIsLoadingPurchases(true)
    setPurchasesError(false)

    clientsService
      .getPurchases(client.id, {
        page,
        per_page: 5,
        date_from: startDate || undefined,
        date_to: endDate || undefined,
      })
      .then((data) => {
        if (active) setPurchases(data)
      })
      .catch(() => {
        if (active) setPurchasesError(true)
        toast.error('No se pudo cargar el historial de compras')
      })
      .finally(() => {
        if (active) setIsLoadingPurchases(false)
      })

    return () => {
      active = false
    }
  }, [client, endDate, open, page, purchasesAttempt, startDate])

  if (!client) return null
  const current = profile || client
  const metrics = current.purchaseMetrics

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreviewImage(null)
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-1rem)] min-w-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-5 py-4 pr-14 sm:px-6">
          <DialogTitle>Perfil del Cliente</DialogTitle>
          <DialogDescription className="sr-only">
            Información detallada del cliente y su historial de compras
          </DialogDescription>
        </DialogHeader>

        <div
          data-slot="customer-detail-body"
          className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6"
        >
          <div className="min-w-0 space-y-6">
            <div className="rounded-lg bg-slate-50 p-6">
              <div className="mb-5 flex items-center gap-4">
                {current.avatarUrl ? (
                  <button
                    type="button"
                    onClick={() => setPreviewImage(current.avatarUrl ?? null)}
                    className="group bg-primary/10 text-primary focus-visible:ring-primary relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full text-2xl font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    aria-label={`Ampliar imagen de ${current.name}`}
                  >
                    <img
                      src={current.avatarUrl}
                      alt=""
                      className="h-full w-full object-cover transition-transform group-hover:scale-110"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                      <Eye className="h-5 w-5 text-white" aria-hidden="true" />
                    </span>
                  </button>
                ) : (
                  <div className="bg-primary/10 text-primary flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full text-2xl font-semibold">
                    {current.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {current.name}
                  </h3>
                  <p className="text-sm text-slate-500">
                    Cliente #{current.id}
                    {isLoadingProfile ? ' · Actualizando...' : ''}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <InfoList label="Emails" values={current.emails} />
                <InfoList label="Teléfonos" values={current.phones} />
                <div className="md:col-span-2">
                  <span className="text-slate-600">Dirección:</span>
                  {current.address ? (
                    <p className="mt-1 font-medium text-slate-900">
                      {current.address.street} {current.address.exteriorNumber}
                      {current.address.interiorNumber
                        ? ` Int. ${current.address.interiorNumber}`
                        : ''}
                      , {current.address.neighborhood},{' '}
                      {current.address.municipality}, {current.address.city},{' '}
                      {current.address.state}, CP {current.address.postalCode},{' '}
                      {current.address.country}
                      {current.address.reference
                        ? ` · ${current.address.reference}`
                        : ''}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-400 italic">
                      No registrada
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-slate-600">Puntos actuales:</span>
                  <p className="mt-1 font-medium text-purple-700">
                    {current.loyalty?.currentTier.medal || '🥉'}{' '}
                    {(current.loyalty?.pointsBalance ?? 0).toLocaleString('es-MX')}{' '}
                    · {current.loyalty?.currentTier.name || 'Bronce'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-600">Puntos históricos:</span>
                  <p className="mt-1 font-medium text-purple-700">
                    {current.loyalty?.lifetimeTier.medal || '🥉'}{' '}
                    {(current.loyalty?.lifetimePoints ?? 0).toLocaleString('es-MX')}{' '}
                    · {current.loyalty?.lifetimeTier.name || 'Bronce'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-600">Saldo de lealtad:</span>
                  <p className="mt-1 font-medium text-green-700">
                    {money.format(current.loyalty?.creditBalance ?? 0)}
                  </p>
                </div>
                {current.notes && (
                  <div className="md:col-span-2">
                    <span className="text-slate-600">Comentarios:</span>
                    <p className="mt-1 font-medium text-slate-900">
                      {current.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Metric
                label="Total neto"
                value={money.format(metrics.netPurchasesTotal)}
                tone="green"
              />
              <Metric
                label="Número de compras"
                value={String(metrics.purchasesCount)}
                tone="blue"
              />
              <Metric
                label="Ticket promedio"
                value={money.format(metrics.averageTicket)}
                tone="purple"
              />
              <Metric
                label="Última compra"
                value={formatDate(metrics.lastPurchaseAt)}
                tone="slate"
              />
            </div>

            <div className="min-w-0">
              <h4 className="mb-3 font-medium text-slate-900">
                Historial de Compras
              </h4>
              <div className="mb-3">
                <GlobalFilterBar
                  show={['dates']}
                  values={{ startDate, endDate }}
                  onChange={(changes) => {
                    if (changes.startDate !== undefined) setStartDate(changes.startDate)
                    if (changes.endDate !== undefined) setEndDate(changes.endDate)
                    setPage(1)
                  }}
                  onClearFilters={() => {
                    setStartDate('')
                    setEndDate('')
                    setPage(1)
                  }}
                  hasActiveFilters={Boolean(startDate || endDate)}
                />
              </div>
              <div className="max-w-full min-w-0 overflow-hidden rounded-lg border border-slate-200">
                <div
                  data-slot="customer-purchases-scroll"
                  className="max-w-full min-w-0 overflow-x-auto overscroll-x-contain"
                >
                  <table className="w-full min-w-[980px] text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Ticket
                        </th>
                        <th className="min-w-[170px] px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Fecha
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Sucursal
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Estado
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Items
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Total original
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Devolución
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-slate-700">
                          Total neto
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {isLoadingPurchases ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-4 py-10 text-center text-slate-500"
                          >
                            Cargando historial...
                          </td>
                        </tr>
                      ) : purchasesError ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center">
                            <Button
                              variant="outline"
                              onClick={() =>
                                setPurchasesAttempt((value) => value + 1)
                              }
                            >
                              Reintentar
                            </Button>
                          </td>
                        </tr>
                      ) : purchases.data.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-4 py-10 text-center text-slate-500"
                          >
                            Este cliente aún no tiene compras registradas.
                          </td>
                        </tr>
                      ) : (
                        purchases.data.map((purchase) => (
                          <tr key={purchase.id} className="hover:bg-slate-50">
                            <td
                              className="px-4 py-3 font-mono text-xs font-medium whitespace-nowrap"
                              title={purchase.ticketNumber}
                            >
                              {formatShortTicket(purchase.ticketNumber)}
                            </td>
                            <td className="min-w-[170px] px-4 py-3 whitespace-nowrap">
                              {formatDate(purchase.completedAt)}
                            </td>
                            <td className="px-4 py-3">
                              {purchase.branchName || '—'}
                            </td>
                            <td className="px-4 py-3">
                              {statusLabel(purchase.status)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {purchase.itemsCount}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {money.format(purchase.originalTotal)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-red-600">
                              {money.format(purchase.refundedTotal)}
                            </td>
                            <td className="px-4 py-3 font-semibold whitespace-nowrap text-green-600">
                              {money.format(purchase.netTotal)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <GlobalPaginator
                  totalItems={purchases.total}
                  itemsPerPage={purchases.perPage}
                  currentPage={purchases.currentPage}
                  from={purchases.from}
                  to={purchases.to}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <ImagePreviewModal
        open={previewImage !== null}
        onOpenChange={(isOpen) => !isOpen && setPreviewImage(null)}
        imageUrl={previewImage}
        altText={`Imagen ampliada de ${current.name}`}
      />
    </Dialog>
  )
}

function InfoList({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <span className="text-slate-600">{label}:</span>
      <div className="mt-1 space-y-1">
        {values.length > 0 ? (
          values.map((value) => (
            <p key={value} className="font-medium text-slate-900">
              {value}
            </p>
          ))
        ) : (
          <p className="text-xs text-slate-400 italic">No registrado</p>
        )}
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone: 'green' | 'blue' | 'purple' | 'slate'
}) {
  const styles = {
    green: 'bg-green-50 text-green-900',
    blue: 'bg-blue-50 text-blue-900',
    purple: 'bg-purple-50 text-purple-900',
    slate: 'bg-slate-100 text-slate-900',
  }
  return (
    <div className={`rounded-lg p-4 ${styles[tone]}`}>
      <p className="text-sm opacity-75">{label}</p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  )
}

function statusLabel(status: string) {
  return (
    (
      {
        COMPLETED: 'Completada',
        PARTIALLY_REFUNDED: 'Devolución parcial',
        REFUNDED: 'Devuelta',
      } as Record<string, string>
    )[status] || status
  )
}
